// src/components/clientes/Financeiro.tsx
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExtendedLead } from './types';
import { financeiroService, ContratoAberto, calcularRecebimentosPrevistos, calcularTotalParcelasPrevistas, ContaPagar, CustoObra, ContaReceber } from '@/services/financeiroService';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthorization } from '@/contexts/AuthorizationContext';

const Financeiro = ({ leadData }: { leadData: ExtendedLead }) => {
  const { hasRole } = useAuthorization();
  const isVendedor = hasRole('vendedor');
  const isParceiro = hasRole('parceiro');
  const ocultarValores = isVendedor || isParceiro;
  
  const [contrato, setContrato] = useState<ContratoAberto | null>(null);
  const [loading, setLoading] = useState(true);
  const [contasReceber, setContasReceber] = useState<ContaReceber[]>([]);
  const [despesas, setDespesas] = useState<ContaPagar[]>([]);
  const [impostos, setImpostos] = useState<CustoObra[]>([]);

  useEffect(() => {
    carregarDados();
  }, [leadData.id]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Buscar contrato do cliente
      const { data: contratosData, error: contratosError } = await supabase
        .from('contratos_sun7')
        .select(`
          id,
          lead_id,
          valor_total,
          integracao_valor_total,
          valor_integracao,
          status,
          created_at,
          integracao_modalidade_pagamento,
          integracao_percentual_assinatura,
          integracao_percentual_inicio_obra,
          integracao_percentual_conclusao_obra,
          integracao_percentual_ligacao_definitiva,
          integracao_valor_assinatura,
          integracao_valor_inicio_obra,
          integracao_valor_conclusao_obra,
          integracao_valor_ligacao_definitiva,
          integracao_forma_pagamento_assinatura,
          integracao_forma_pagamento_inicio_obra,
          integracao_forma_pagamento_conclusao_obra,
          integracao_forma_pagamento_ligacao_definitiva,
          integracao_numero_parcelas_assinatura,
          integracao_numero_parcelas_inicio_obra,
          integracao_numero_parcelas_conclusao_obra,
          integracao_numero_parcelas_ligacao_definitiva,
          integracao_valor_parcela_assinatura,
          integracao_valor_parcela_inicio_obra,
          integracao_valor_parcela_conclusao_obra,
          integracao_valor_parcela_ligacao_definitiva
        `)
        .eq('lead_id', leadData.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (contratosError) {
        if (contratosError.code === 'PGRST116') {
          // Nenhum contrato encontrado
          setContrato(null);
          setLoading(false);
          return;
        }
        throw contratosError;
      }

      if (!contratosData) {
        setContrato(null);
        setLoading(false);
        return;
      }

      // Buscar valor recebido e parcelas pagas
      const contasReceberData = await financeiroService.listarContasReceber();
      setContasReceber(contasReceberData);
      
      const contasDoContrato = contasReceberData.filter(c => c.contrato_id === contratosData.id && c.status === 'recebido');
      const valorRecebido = contasDoContrato.reduce((sum, c) => sum + c.valor, 0);
      const parcelasPagas = contasDoContrato.length;

      // Montar objeto ContratoAberto
      const valorIntegracao = contratosData.integracao_valor_total || contratosData.valor_integracao || 0;
      const clienteNome = leadData.nome || leadData.razao_social || 'Cliente';
      
      const contratoAbertoTemp: ContratoAberto = {
        id: contratosData.id,
        lead_id: contratosData.lead_id,
        cliente_nome: clienteNome,
        valor_total: contratosData.valor_total || 0,
        valor_recebido: valorRecebido,
        valor_pendente: valorIntegracao - valorRecebido,
        parcelas_pagas: parcelasPagas,
        parcelas_total: 0, // Será calculado abaixo
        status: contratosData.status,
        created_at: contratosData.created_at,
        integracao_modalidade_pagamento: contratosData.integracao_modalidade_pagamento,
        integracao_valor_total: valorIntegracao,
        integracao_percentual_assinatura: contratosData.integracao_percentual_assinatura,
        integracao_percentual_inicio_obra: contratosData.integracao_percentual_inicio_obra,
        integracao_percentual_conclusao_obra: contratosData.integracao_percentual_conclusao_obra,
        integracao_percentual_ligacao_definitiva: contratosData.integracao_percentual_ligacao_definitiva,
        integracao_valor_assinatura: contratosData.integracao_valor_assinatura,
        integracao_valor_inicio_obra: contratosData.integracao_valor_inicio_obra,
        integracao_valor_conclusao_obra: contratosData.integracao_valor_conclusao_obra,
        integracao_valor_ligacao_definitiva: contratosData.integracao_valor_ligacao_definitiva,
        integracao_forma_pagamento_assinatura: contratosData.integracao_forma_pagamento_assinatura,
        integracao_forma_pagamento_inicio_obra: contratosData.integracao_forma_pagamento_inicio_obra,
        integracao_forma_pagamento_conclusao_obra: contratosData.integracao_forma_pagamento_conclusao_obra,
        integracao_forma_pagamento_ligacao_definitiva: contratosData.integracao_forma_pagamento_ligacao_definitiva,
        integracao_numero_parcelas_assinatura: contratosData.integracao_numero_parcelas_assinatura,
        integracao_numero_parcelas_inicio_obra: contratosData.integracao_numero_parcelas_inicio_obra,
        integracao_numero_parcelas_conclusao_obra: contratosData.integracao_numero_parcelas_conclusao_obra,
        integracao_numero_parcelas_ligacao_definitiva: contratosData.integracao_numero_parcelas_ligacao_definitiva,
        integracao_valor_parcela_assinatura: contratosData.integracao_valor_parcela_assinatura,
        integracao_valor_parcela_inicio_obra: contratosData.integracao_valor_parcela_inicio_obra,
        integracao_valor_parcela_conclusao_obra: contratosData.integracao_valor_parcela_conclusao_obra,
        integracao_valor_parcela_ligacao_definitiva: contratosData.integracao_valor_parcela_ligacao_definitiva,
      };
      
      // Calcular total de parcelas
      contratoAbertoTemp.parcelas_total = calcularTotalParcelasPrevistas(contratoAbertoTemp);
      
      const contratoAberto = contratoAbertoTemp;

      setContrato(contratoAberto);

      // Buscar despesas do contrato
      const despesasData = await financeiroService.listarContasPagar({ contrato_id: contratosData.id });
      setDespesas(despesasData);

      // Buscar impostos (custos de obras com categoria "Impostos")
      const custos = await financeiroService.listarCustosObras({ contrato_id: contratosData.id });
      const impostosData = custos.filter(c => c.categoria === 'Impostos');
      setImpostos(impostosData);
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'assinado':
        return <Badge variant="default" className="bg-success">Assinado</Badge>;
      case 'em_andamento':
        return <Badge variant="outline" className="border-blue-500 text-blue-500">Em Andamento</Badge>;
      case 'finalizado':
        return <Badge variant="default" className="bg-success text-white">Finalizado</Badge>;
      case 'contrato_gerado':
        return <Badge variant="secondary">Contrato Gerado</Badge>;
      case 'proposta_aceita':
        return <Badge variant="outline">Proposta Aceita</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatarModalidade = (modalidade?: number): string => {
    const modalidades: { [key: number]: string } = {
      1: 'Pagamento integral na assinatura',
      2: 'Assinatura + Conclusão',
      3: 'Assinatura + Início + Conclusão',
      4: 'Assinatura + Início + Conclusão + Ligação',
      5: 'Pagamento integral na conclusão da obra'
    };
    return modalidades[modalidade || 1] || 'Não informado';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calcularPercentual = (valor: number, receita: number): number => {
    if (receita === 0) return 0;
    return (valor / receita) * 100;
  };

  const formatPercentual = (percentual: number): string => {
    return `${percentual.toFixed(2)}%`;
  };

  const verificarPagamentoConfirmado = (contratoId: string, descricaoEtapa: string): boolean => {
    const descricaoBase = descricaoEtapa.split(' - Parcela')[0];
    return contasReceber.some(conta => 
      conta.contrato_id === contratoId && 
      conta.status === 'recebido' &&
      (conta.descricao.includes(descricaoBase) || conta.descricao.includes(descricaoEtapa))
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum contrato encontrado para este cliente</p>
      </div>
    );
  }

  const recebimentosPrevistos = calcularRecebimentosPrevistos(contrato);
  const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
  const totalDespesas = despesas.reduce((acc, d) => acc + (d.valor || 0), 0);
  const totalImpostos = impostos.reduce((acc, i) => acc + i.valor, 0);
  const resultado = totalReceitas - totalDespesas - totalImpostos;
  const isLucro = resultado >= 0;
  const percentualLucro = calcularPercentual(Math.abs(resultado), totalReceitas);
  const percentualDespesas = calcularPercentual(totalDespesas, totalReceitas);
  const percentualImpostos = calcularPercentual(totalImpostos, totalReceitas);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Informações Financeiras do Contrato</h2>
        <p className="text-muted-foreground">Detalhes financeiros do contrato do cliente</p>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Valor do Contrato</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(contrato.valor_total)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Valor Integração</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(contrato.integracao_valor_total || 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Valor Recebido</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {formatCurrency(contrato.valor_recebido)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Valor Pendente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {formatCurrency(contrato.valor_pendente)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Informações do Contrato */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Contrato</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Cliente</p>
              <p className="font-medium">{contrato.cliente_nome}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Data</p>
              <p className="font-medium">
                {contrato.created_at 
                  ? format(new Date(contrato.created_at), "dd/MM/yyyy", { locale: ptBR })
                  : '-'
                }
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <div>{getStatusBadge(contrato.status)}</div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Modalidade</p>
              <p className="text-xs">{formatarModalidade(contrato.integracao_modalidade_pagamento)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Parcelas</p>
              <p className="font-medium">{contrato.parcelas_pagas} / {contrato.parcelas_total}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receitas */}
      {recebimentosPrevistos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Receitas</CardTitle>
            <CardDescription>Recebimentos previstos do contrato</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recebimentosPrevistos.map((recebimento, index) => (
                <div
                  key={`${recebimento.etapa}-${index}`}
                  className="flex items-center justify-between p-3 bg-background rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{recebimento.descricao}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {recebimento.percentual > 0 && (
                        <span>{recebimento.percentual}% • </span>
                      )}
                      {recebimento.forma_pagamento}
                      {recebimento.numero_parcelas > 1 && (
                        <span> • {recebimento.numero_parcelas}x de {formatCurrency(recebimento.valor_parcela)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-semibold text-sm">
                        {formatCurrency(recebimento.valor)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Recebido?</span>
                      <span className={`text-sm font-semibold ${
                        verificarPagamentoConfirmado(contrato.id, recebimento.descricao)
                          ? 'text-success'
                          : 'text-muted-foreground'
                      }`}>
                        {verificarPagamentoConfirmado(contrato.id, recebimento.descricao) ? 'Sim' : 'Não'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20 mt-2">
                <div className="font-semibold text-sm">Total de Receitas Previstas</div>
                <div className="font-bold text-sm text-primary">
                  {formatCurrency(totalReceitas)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Despesas */}
      <Card>
        <CardHeader>
          <CardTitle>Despesas</CardTitle>
          <CardDescription>Despesas vinculadas ao contrato</CardDescription>
        </CardHeader>
        <CardContent>
          {despesas.length === 0 ? (
            <div className="p-3 bg-background rounded-lg border text-sm text-muted-foreground">
              Nenhuma despesa vinculada a este contrato
            </div>
          ) : (
            <div className="space-y-2">
              {despesas.map((despesa) => (
                <div
                  key={despesa.id}
                  className="flex items-center justify-between p-3 bg-background rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{despesa.descricao}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {despesa.categoria}
                      {despesa.fornecedor && <span> • {despesa.fornecedor}</span>}
                      {despesa.data_pagamento && (
                        <span> • Pago em {format(new Date(despesa.data_pagamento), "dd/MM/yyyy", { locale: ptBR })}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm text-destructive">
                      {ocultarValores ? '---' : formatCurrency(despesa.valor || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {despesa.status === 'pago' ? 'Pago' : despesa.status === 'pendente' ? 'Pendente' : 'Vencido'}
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg border border-destructive/20 mt-2">
                <div className="font-semibold text-sm">Total de Despesas</div>
                <div className="text-right">
                  <div className="font-bold text-sm text-destructive">
                    {ocultarValores ? '---' : formatCurrency(totalDespesas)}
                  </div>
                  {!ocultarValores && totalReceitas > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatPercentual(percentualDespesas)} da receita
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Impostos */}
      <Card>
        <CardHeader>
          <CardTitle>Impostos</CardTitle>
          <CardDescription>Impostos vinculados ao contrato</CardDescription>
        </CardHeader>
        <CardContent>
          {impostos.length === 0 ? (
            <div className="p-3 bg-background rounded-lg border text-sm text-muted-foreground">
              Nenhum imposto registrado para este contrato
            </div>
          ) : (
            <div className="space-y-2">
              {impostos.map((imposto) => (
                <div
                  key={imposto.id}
                  className="flex items-center justify-between p-3 bg-background rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{imposto.descricao}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {format(new Date(imposto.data), "dd/MM/yyyy", { locale: ptBR })}
                      {imposto.observacoes && <span> • {imposto.observacoes}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm text-orange-600">
                      {ocultarValores ? '---' : formatCurrency(imposto.valor)}
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800 mt-2">
                <div className="font-semibold text-sm">Total de Impostos</div>
                <div className="text-right">
                  <div className="font-bold text-sm text-orange-600">
                    {ocultarValores ? '---' : formatCurrency(totalImpostos)}
                  </div>
                  {!ocultarValores && totalReceitas > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatPercentual(percentualImpostos)} da receita
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resultado (Lucro/Prejuízo) */}
      <Card>
        <CardHeader>
          <CardTitle>Resultado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${
            isLucro 
              ? 'bg-success/10 border-success/30' 
              : 'bg-destructive/10 border-destructive/30'
          }`}>
            <div className="font-semibold text-base">
              {isLucro ? 'Lucro' : 'Prejuízo'}
            </div>
            <div className="text-right">
              <div className={`font-bold text-lg ${
                isLucro ? 'text-success' : 'text-destructive'
              }`}>
                {ocultarValores ? '---' : formatCurrency(Math.abs(resultado))}
              </div>
              {!ocultarValores && totalReceitas > 0 && (
                <div className={`text-sm mt-1 ${
                  isLucro ? 'text-success' : 'text-destructive'
                }`}>
                  {formatPercentual(percentualLucro)} da receita
                </div>
              )}
            </div>
          </div>
          {!ocultarValores && (
            <div className="text-xs text-muted-foreground mt-2 text-center">
              Receitas: {formatCurrency(totalReceitas)} - Despesas: {formatCurrency(totalDespesas)} - Impostos: {formatCurrency(totalImpostos)} = {isLucro ? 'Lucro' : 'Prejuízo'}: {formatCurrency(Math.abs(resultado))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Financeiro;